import { Dispatch, useState } from "react";
import { FieldValues } from "react-hook-form";
import { FieldErrors } from "react-hook-form/dist/types/errors";
import { BACKEND_URL } from "../../../constants/env";
import { useToastStore } from "../../config/store/toastStore";
import { useTranslation } from "next-i18next";
import {
  queryClient,
  QueryKeys,
} from "../../../hooks/react-query/query-client";
import { useFirebaseAuth } from "../../../hooks/firebase/useFirebaseAuth";

export function useRegistration() {
  const { setToast } = useToastStore();
  const [error, setError] = useState<string>("");
  const [processing, setProcessing] = useState<boolean>(false);
  const { t } = useTranslation("common");
  const { logInWithEmailAndPassword, returnError } = useFirebaseAuth();

  async function registerUser(
    values: FieldValues,
    errors: FieldErrors<any>,
    closeModal: () => void,
    autoSignIn?: boolean
  ) {
    setError("");
    const formIsValid = Object.keys(errors).length === 0;
    if (formIsValid) {
      setProcessing(true);
      try {
        const response = await registerUserOnServer(values);
        const responseStatus = response.status;
        switch (responseStatus) {
          case 201:
            closeModal();
            setToast(t("success"), t("login.registrationSuccess"), 10000);
            if (autoSignIn) {
              await login({ email: values.email, password: values.password });
            }
            break;
          case 422:
            setError(t("formErrors").toString());
            break;
          case 500:
            const serverResponse = await response.json();
            switch (serverResponse.message) {
              case "ALREADY_EXISTS":
                setError(
                  t("login.registrationErrorAlreadyRegistered").toString()
                );
                break;
              default:
                setError(t("login.registrationError").toString());
            }
            break;
          default:
            setError(t("login.registrationError").toString());
        }

        setProcessing(false);
      } catch (err) {
        console.log(err);
        setProcessing(false);
        setError(t("login.registrationError").toString());
      }
    } else {
      setError(t("formErrors").toString());
    }
  }

  async function login(form: { email: string; password: string }) {
    setProcessing(true);
    setError("");
    try {
      const serverResponse = await logInWithEmailAndPassword(
        form.email,
        form.password
      );

      if (typeof serverResponse === "string") {
        setError(returnError(serverResponse));
        setProcessing(false);
      } else {
        await queryClient.invalidateQueries(QueryKeys.USER);
        await queryClient.invalidateQueries(QueryKeys.PAST_APPOINTMENTS);
        await queryClient.invalidateQueries(QueryKeys.APPOINTMENTS);
        await queryClient.invalidateQueries(QueryKeys.JWT);
        setProcessing(false);
      }
    } catch (err) {
      console.error(err);
      // @ts-ignore
      setError(returnError(err.toString()));
      setProcessing(false);
    }
  }

  async function registerUserOnServer(form: FieldValues) {
    return await fetch(`${BACKEND_URL}/auth/create`, {
      method: "POST",
      headers: {
        "Content-type": "application/json",
      },
      body: JSON.stringify(form),
    });
  }

  return {
    error,
    processing,
    registerUser,
  };
}
